import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, Card } from '@/components/common';

const educationSchema = z.object({
  institution: z.string().min(1, 'Institution name is required'),
  degree: z.string().min(1, 'Degree is required'),
  field: z.string().optional(),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().optional(),
  current: z.boolean().optional(),
});

export type EducationFormData = z.infer<typeof educationSchema>;

export interface Education extends Omit<EducationFormData, 'current'> {
  id?: string;
}

interface EducationEditorProps {
  education: Education[];
  onChange: (education: Education[]) => void;
  disabled?: boolean;
}

export function EducationEditor({ education, onChange, disabled = false }: EducationEditorProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<EducationFormData>({
    resolver: zodResolver(educationSchema),
  });

  const isCurrent = watch('current');

  const handleAdd = () => {
    setIsAdding(true);
    setEditingIndex(null);
    reset({
      institution: '',
      degree: '',
      field: '',
      startDate: '',
      endDate: '',
      current: false,
    });
  };

  const handleEdit = (index: number) => {
    const edu = education[index];
    setEditingIndex(index);
    setIsAdding(false);
    reset({
      ...edu,
      current: !edu.endDate,
    });
  };

  const handleDelete = (index: number) => {
    const updated = education.filter((_, i) => i !== index);
    onChange(updated);
  };

  const onSubmit = (data: EducationFormData) => {
    const { current, ...eduData } = data;
    const educationEntry: Education = {
      ...eduData,
      endDate: current ? undefined : eduData.endDate,
    };

    if (editingIndex !== null) {
      // Update existing
      const updated = [...education];
      updated[editingIndex] = { ...updated[editingIndex], ...educationEntry };
      onChange(updated);
    } else {
      // Add new
      onChange([...education, educationEntry]);
    }

    setIsAdding(false);
    setEditingIndex(null);
    reset();
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingIndex(null);
    reset();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Education</h3>
        {!isAdding && editingIndex === null && (
          <Button size="sm" onClick={handleAdd} disabled={disabled}>
            + Add Education
          </Button>
        )}
      </div>

      {/* Education List */}
      {!isAdding && editingIndex === null && (
        <div className="space-y-3">
          {education.length === 0 ? (
            <p className="text-text-muted text-sm">No education added yet</p>
          ) : (
            education.map((edu, index) => (
              <Card key={index} className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-semibold text-text-primary">{edu.degree}</h4>
                    {edu.field && <p className="text-text-secondary">{edu.field}</p>}
                    <p className="text-text-secondary">{edu.institution}</p>
                    <p className="text-sm text-text-muted">
                      {edu.startDate} - {edu.endDate || 'Present'}
                    </p>
                  </div>
                  <div className="flex gap-1 ml-4 flex-shrink-0">
                    <button
                      onClick={() => handleEdit(index)}
                      disabled={disabled}
                      className="text-primary hover:text-primary-dark text-sm px-2 py-1.5 rounded hover:bg-primary-soft min-h-[44px] flex items-center"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(index)}
                      disabled={disabled}
                      className="text-red-600 hover:text-red-800 text-sm px-2 py-1.5 rounded hover:bg-error-soft min-h-[44px] flex items-center"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Add/Edit Form */}
      {(isAdding || editingIndex !== null) && (
        <Card className="p-4">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <h4 className="font-semibold text-text-primary">
              {editingIndex !== null ? 'Edit Education' : 'Add Education'}
            </h4>

            <Input
              label="Institution *"
              error={errors.institution?.message}
              fullWidth
              placeholder="University name"
              {...register('institution')}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Degree *"
                error={errors.degree?.message}
                fullWidth
                placeholder="Bachelor's, Master's, etc."
                {...register('degree')}
              />
              <Input
                label="Field of Study"
                error={errors.field?.message}
                fullWidth
                placeholder="Computer Science, etc."
                {...register('field')}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Start Date *"
                type="month"
                error={errors.startDate?.message}
                fullWidth
                {...register('startDate')}
              />
              <Input
                label="End Date"
                type="month"
                error={errors.endDate?.message}
                fullWidth
                disabled={isCurrent}
                {...register('endDate')}
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="current-edu"
                className="mr-2"
                {...register('current')}
                onChange={(e) => {
                  setValue('current', e.target.checked);
                  if (e.target.checked) {
                    setValue('endDate', '');
                  }
                }}
              />
              <label htmlFor="current-edu" className="text-sm text-text-secondary">
                I currently study here
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit">{editingIndex !== null ? 'Update' : 'Add'} Education</Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
